// Regras puras da tela de Integrações (/queue-integration).
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";
import { AEROS } from "../../styles/aerosTokens";

/**
 * Tipos de integração. A CHAVE é o valor gravado em `QueueIntegrations.type`
 * e é contrato do backend — repare que SGP é gravado em MAIÚSCULAS, diferente
 * dos demais. Não renomear.
 */
export const INTEGRATION_TYPES = [
  { key: "typebot", icon: "smart_toy", color: AEROS.cyan, group: "bot" },
  { key: "n8n", icon: "device_hub", color: AEROS.error, group: "webhook" },
  { key: "webhook", icon: "webhook", color: AEROS.warning, group: "webhook" },
  { key: "dialogflow", icon: "psychology", color: AEROS.success, group: "bot" },
  { key: "flowbuilder", icon: "account_tree", color: "#C08BFF", group: "bot" },
  { key: "SGP", icon: "receipt_long", color: "#FB923C", group: "system" },
];

const FALLBACK = {
  key: "webhook",
  icon: "webhook",
  color: AEROS.warning,
  group: "webhook",
};

/**
 * Metadados do tipo, tolerante a caixa.
 *
 * Registros antigos gravaram "sgp" minúsculo em algumas bases; comparar sem
 * normalizar deixaria esses cards com o ícone e a cor errados.
 */
export const typeMeta = (type) => {
  if (!type) return FALLBACK;
  const alvo = String(type).toLowerCase();
  return (
    INTEGRATION_TYPES.find((t) => t.key.toLowerCase() === alvo) || FALLBACK
  );
};

export const typeLabel = (type) =>
  i18n.t(`aeros.integrations.types.${typeMeta(type).key.toLowerCase()}`);

/**
 * Rótulos das conexões e filas vinculadas, para os chips do card.
 *
 * Conexão vem antes de fila porque é o vínculo mais forte: uma integração
 * presa a uma conexão vale para tudo que entra por ela.
 */
export const linkChips = (links) => {
  if (!links) return [];
  const conexoes = (links.whatsapps || []).map((w) =>
    i18n.t("aeros.integrations.linkConnection", { name: w.name })
  );
  const filas = (links.queues || []).map((q) =>
    i18n.t("aeros.integrations.linkQueue", { name: q.name })
  );
  return [...conexoes, ...filas];
};

/**
 * Uma integração sem conexão nem fila apontando para ela nunca é acionada.
 * O card avisa isso em vez de dizer "Ativa" para algo que não roda.
 */
export const integrationStatus = (links) =>
  linkChips(links).length === 0 ? "unlinked" : "linked";

/**
 * Linha em monoespaçado do card: o "endereço" da integração, que muda de
 * significado conforme o tipo. Sem nada configurado, o card diz isso em vez
 * de mostrar uma faixa vazia.
 */
export const endpointLabel = (integration = {}) => {
  const { key } = typeMeta(integration.type);
  if (key === "typebot") {
    return [integration.urlN8N, integration.typebotSlug].filter(Boolean).join(" · ");
  }
  if (key === "n8n" || key === "webhook") return integration.urlN8N || "";
  if (key === "dialogflow") {
    return [integration.projectName, integration.language].filter(Boolean).join(" · ");
  }
  if (key === "SGP") {
    try {
      const dados = integration.jsonContent ? JSON.parse(integration.jsonContent) : {};
      return dados.sgpUrl || "";
    } catch (err) {
      // jsonContent inválido não pode derrubar a listagem inteira.
      return "";
    }
  }
  return i18n.t("aeros.integrations.internalFlow");
};

// Chips de filtro do topo. "all" não filtra nada; os demais somam por grupo.
export const FILTERS = ["all", "bot", "webhook", "system", "unlinked"];

export const matchesFilter = (integration, filtro) => {
  if (!filtro || filtro === "all") return true;
  if (filtro === "unlinked") return integrationStatus(integration.links) === "unlinked";
  return typeMeta(integration.type).group === filtro;
};

export const filterCounts = (integrations = []) =>
  FILTERS.reduce((contagem, filtro) => {
    contagem[filtro] = integrations.filter((i) => matchesFilter(i, filtro)).length;
    return contagem;
  }, {});

/**
 * "200 OK · 412 ms" do resultado do teste de webhook. Sem status, a
 * requisição não chegou ao servidor do cliente — dizer "erro 0" confundiria.
 */
export const testResultLabel = ({ status, statusText, ms } = {}) => {
  const codigo = status
    ? [status, statusText].filter(Boolean).join(" ")
    : i18n.t("aeros.integrations.test.noResponse");
  return [codigo, `${ms} ms`].filter(Boolean).join(" · ");
};
